import { createI18n, LocaleMessages, VueMessageType } from 'vue-i18n';

/**
 * Load global locale messages from the locales directory
 */
function loadGlobalLocaleMessages(): Record<string, LocaleMessages<VueMessageType>> {
  const locales = require.context(
    './locales',
    true,
    /[A-Za-z0-9-_,\s]+\.json$/i
  );
  const messages: Record<string, LocaleMessages<VueMessageType>> = {};
  locales.keys().forEach((key: string) => {
    const matched = key.match(/([A-Za-z0-9-_]+)\./i);
    if (matched && matched.length > 1) {
      const locale = matched[1];
      if (!messages[locale]) {
        messages[locale] = {};
      }
      messages[locale] = {
        ...messages[locale],
        ...locales(key).default
      };
    }
  });
  return messages;
}

/**
 * Load locale messages from the views directory
 */
function loadViewsLocaleMessages(): Record<string, LocaleMessages<VueMessageType>> {
  const locales = require.context('@/views', true, /\.json$/i);
  const messages: Record<string, LocaleMessages<VueMessageType>> = {};

  locales.keys().forEach((key: string) => {
    const matched = key.match(/\/([A-Za-z0-9-_]+)\.json$/i);
    if (matched && matched.length > 1) {
      const locale = matched[1];
      if (!messages[locale]) {
        messages[locale] = {};
      }
      messages[locale] = {
        ...messages[locale],
        ...locales(key).default
      };
    }
  });

  return messages;
}

/**
 * Merge messages from both locales and views directories
 */
function loadLocaleMessages(): Record<string, LocaleMessages<VueMessageType>> {
  const globalMessages = loadGlobalLocaleMessages();
  const viewsMessages = loadViewsLocaleMessages();

  const messages: Record<string, LocaleMessages<VueMessageType>> = { ...globalMessages };

  Object.keys(viewsMessages).forEach((locale) => {
    if (!messages[locale]) {
      messages[locale] = {};
    }
    messages[locale] = {
      ...messages[locale],
      ...viewsMessages[locale]
    };
  });

  return messages;
}

/**
 * Load component-specific locale messages
 */
export async function loadComponentLocaleMessages(locale: string, componentName: string) {
  try {
    const messages = await import(`@/views/${componentName}/${locale}.json`);
    const currentMessages = i18n.global.getLocaleMessage(locale);
    i18n.global.setLocaleMessage(locale, {
      ...currentMessages,
      ...messages.default,
    });
  } catch (error) {
    console.error(`Failed to load locale messages for component ${componentName} and locale ${locale}:`, error);
  }
}

/**
 * Custom missing handler to check in views directory if not found in locales
 */
function handleMissing(locale: string, key: string) {
  const keys = key.split('.');
  const viewsMessages = loadViewsLocaleMessages();
  let message: any = viewsMessages[locale];

  for (const k of keys) {
    if (message && message[k]) {
      message = message[k];
    } else {
      return key; // Return the key itself if not found
    }
  }
  return message;
}

const messages = loadLocaleMessages();

const i18n = createI18n({
  legacy: false,
  locale: process.env.VUE_APP_I18N_LOCALE || 'en',
  fallbackLocale: process.env.VUE_APP_I18N_FALLBACK_LOCALE || 'en',
  messages,
  missing: (locale, key) => {
    return handleMissing(locale, key);
  }
});

export default i18n;
